import { Search, Store, Grid3X3, List, ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { browseFranchises, FranchiseDoc } from "../../interfaces/establishments";
import { getBundledLogo } from "../../utils/franchiseDetector";
import { NewsCategoryTag } from "../../components/franchise/NewsCategoryTag";
import { BrandStack } from "../../components/franchise/FranchiseLogo";
import { StatCard } from "../../components/ui/Card";
import { DropdownMenu, DropdownItem } from "../../components/ui/DropdownMenu";
import { useDebouncedEffect } from "../../hooks/useDebouncedEffect";
import TrialLockBanner from "../../components/TrialLockBanner";
import { isTrialUser, trialVisibleCount, TRIAL_LOCKED_CLASS } from "../../utils/trial";
import { BuildingStorefrontIcon } from "@heroicons/react/24/solid";
import { PageHeader } from "../../components/ui/PageHeader";

const PAGE_SIZE = 24;

const AVATAR_COLORS = [
    "#1A1A1A", "#7C2D12", "#1E3A5F", "#065F46", "#7C3AED",
    "#831843", "#374151", "#5C3317", "#1A3A2A", "#4A1942",
];

function nameToColor(name: string): string {
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
        hash = (hash << 5) - hash + name.charCodeAt(i);
        hash |= 0;
    }
    return AVATAR_COLORS[Math.abs(hash) % AVATAR_COLORS.length];
}

function franchiseDomain(f: FranchiseDoc): string | null {
    const url = f.website;
    if (!url) return null;
    try {
        return new URL(url.startsWith("http") ? url : `https://${url}`).hostname.replace(/^www\./, "");
    } catch {
        return null;
    }
}

function FranchiseLogo({ f, size = 48 }: { f: FranchiseDoc; size?: number }) {
    // Bundled logo → back-office logoUrl → Clearbit → initial: a broken stored URL falls through to
    // Clearbit instead of straight to the initial (same chain as the fiche's FranchiseLogo).
    const [attempt, setAttempt] = useState(0);
    const style = { width: size, height: size };
    const color = nameToColor(f.name);

    // A bundled image only: the detector's emoji fallback is not a logo and must not hide the
    // back-office URL or Clearbit.
    const bundled = getBundledLogo(f.name);
    if (bundled) {
        return <img src={bundled} alt="" className="rounded-lg object-cover flex-shrink-0" style={style} />;
    }

    const domain = franchiseDomain(f);
    const candidates = [f.logoUrl, domain ? `https://logo.clearbit.com/${domain}` : null].filter(Boolean) as string[];
    const src = candidates[attempt];

    if (src) {
        return <img key={src} src={src} alt="" className="rounded-lg object-cover flex-shrink-0" style={style} onError={() => setAttempt((a) => a + 1)} />;
    }

    return (
        <div className="rounded-lg flex items-center justify-center flex-shrink-0" style={{ ...style, backgroundColor: color }}>
            <span className="font-bold text-white" style={{ fontSize: size * 0.38 }}>
                {f.name[0]?.toUpperCase()}
            </span>
        </div>
    );
}

type SortKey = "activity" | "count" | "name";

const SORT_OPTIONS: { key: SortKey; label: string }[] = [
    { key: "activity", label: "Activité récente" },
    { key: "count", label: "Établissements" },
    { key: "name", label: "Nom" },
];

// 2+ is the floor, not a filter: a "franchise" with a single establishment is not a network,
// and one with none is a tagging artefact. Both are excluded server-side.
const MIN_ESTABLISHMENTS_FLOOR = 2;


export default function Franchiseurs() {
    const navigate = useNavigate();
    const [franchises, setFranchises] = useState<FranchiseDoc[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState("");
    // Biggest networks first — the news figures are shown on each card but never decide the
    // order unless the user picks "Activité récente".
    const [sortKey, setSortKey] = useState<SortKey>("count");
    const [viewMode, setViewMode] = useState<"grid" | "list">("grid");
    const [showSort, setShowSort] = useState(false);
    const [page, setPage] = useState(1);

    const [total, setTotal] = useState(0);
    const [totalPages, setTotalPages] = useState(1);
    const [stats, setStats] = useState({ franchises: 0, totalEstablishments: 0, totalNews3Months: 0, openings: 0 });

    // Reset to page 1 on any search/sort change, so a narrower result set never leaves the
    // user stranded on a page that no longer exists.
    useEffect(() => { setPage(1); }, [searchTerm, sortKey]);

    // Debounced so typing in the search box doesn't fire a request per keystroke.
    const [debouncedSearch, setDebouncedSearch] = useState("");
    useEffect(() => {
        const t = setTimeout(() => setDebouncedSearch(searchTerm), 300);
        return () => clearTimeout(t);
    }, [searchTerm]);

    // The server decides what exists, in what order, and what belongs on this page. Nothing
    // below re-filters or re-sorts: doing that here would make each page an independent slice
    // and break continuity between them.
    useDebouncedEffect(() => {
        let cancelled = false;
        setLoading(true);
        browseFranchises({
            search: debouncedSearch || undefined,
            minEstablishments: MIN_ESTABLISHMENTS_FLOOR,
            sort: sortKey,
            page,
            limit: PAGE_SIZE,
        })
            .then((res) => {
                if (cancelled) return;
                setFranchises(res.items);
                setTotal(res.total);
                setTotalPages(res.totalPages);
                setStats(res.stats);
                // The server clamps an out-of-range page; follow it so the controls stay honest.
                if (res.page !== page) setPage(res.page);
            })
            .catch(() => { if (!cancelled) setFranchises([]); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [debouncedSearch, sortKey, page]);

    const { totalEstablishments, totalNews3Months, openings } = stats;
    // Trial users get the first page only, a third of it readable.
    const isTrial = isTrialUser();
    const visibleCount = trialVisibleCount(franchises.length, isTrial);

    const currentSortLabel = SORT_OPTIONS.find((o) => o.key === sortKey)?.label ?? "Établissements";

    return (
        <div className="min-h-screen bg-canvas font-sans">
            <div className="mx-auto max-w-7xl px-8 pb-6">
                <PageHeader icon={<BuildingStorefrontIcon />} title="Franchiseurs" subtitle="Base de données des franchiseurs français en restauration" />

                {/* KPI row */}
                <div className="flex gap-4 mb-6">
                    <StatCard className="flex-1 min-w-0" label="Franchiseurs" value={stats.franchises.toLocaleString("fr-FR")} />
                    <StatCard className="flex-1 min-w-0" label="Établissements" value={totalEstablishments.toLocaleString("fr-FR")} />
                    <StatCard className="flex-1 min-w-0" label="Actualités" value={totalNews3Months.toLocaleString("fr-FR")} />
                    <StatCard className="flex-1 min-w-0" label="Avec ouvertures récentes" value={openings.toLocaleString("fr-FR")} />
                </div>

                {/* Search row */}
                <div className="bg-surface border border-border rounded-2xl px-4 py-3 mb-0 flex items-center gap-3">
                    <div className="relative flex-1 min-w-0">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint" />
                        <input
                            type="text"
                            placeholder="Rechercher une franchise, une enseigne…"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-9 pr-4 py-1.5 text-sm text-ink placeholder:text-ink-faint focus:outline-none"
                        />
                    </div>

                    <div className="flex items-center gap-1.5 flex-shrink-0">
                        <button
                            onClick={() => setViewMode("grid")}
                            className={`p-1.5 rounded-lg transition-colors duration-150 ${viewMode === "grid" ? "bg-surface-sunken text-ink" : "text-ink-faint hover:text-ink"}`}
                        >
                            <Grid3X3 className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setViewMode("list")}
                            className={`p-1.5 rounded-lg transition-colors duration-150 ${viewMode === "list" ? "bg-surface-sunken text-ink" : "text-ink-faint hover:text-ink"}`}
                        >
                            <List className="w-4 h-4" />
                        </button>
                    </div>
                </div>

                {/* Results bar */}
                <div className="flex items-center justify-between mt-4 mb-4">
                    <p className="text-sm text-ink-muted">
                        <span className="font-semibold text-ink">{total}</span> franchise{total > 1 ? "s" : ""}
                        {totalPages > 1 && (
                            <> · page <span className="font-semibold text-ink">{page}</span> / {totalPages}</>
                        )}
                    </p>
                    <div className="relative">
                        <button
                            onClick={() => setShowSort((v) => !v)}
                            className="flex items-center gap-1 text-sm text-ink-muted hover:text-ink transition-colors duration-150"
                        >
                            Trier par{" "}
                            <span className="font-semibold text-ink">{currentSortLabel}</span>
                            <ChevronDown className="w-3.5 h-3.5" />
                        </button>
                        {showSort && (
                            <DropdownMenu className="absolute right-0 top-7 z-10 min-w-[180px]">
                                {SORT_OPTIONS.map((opt) => (
                                    <DropdownItem
                                        key={opt.key}
                                        onClick={() => { setSortKey(opt.key); setShowSort(false); }}
                                        className={sortKey === opt.key ? "font-semibold" : ""}
                                    >
                                        {opt.label}
                                    </DropdownItem>
                                ))}
                            </DropdownMenu>
                        )}
                    </div>
                </div>

                {/* Cards */}
                {loading ? (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <div key={i} className="bg-surface border border-border rounded-2xl p-5 motion-safe:animate-pulse">
                                <div className="flex items-center gap-3 mb-4">
                                    <div className="w-12 h-12 rounded-lg bg-surface-sunken flex-shrink-0" />
                                    <div className="flex-1 space-y-2">
                                        <div className="h-4 bg-surface-sunken rounded-full w-2/3" />
                                        <div className="h-3 bg-surface-sunken rounded-full w-1/3" />
                                    </div>
                                </div>
                                <div className="grid grid-cols-2 gap-3 mb-4">
                                    <div className="h-10 bg-surface-sunken rounded-lg" />
                                    <div className="h-10 bg-surface-sunken rounded-lg" />
                                </div>
                                <div className="h-3 bg-surface-sunken rounded-full w-1/2" />
                            </div>
                        ))}
                    </div>
                ) : total === 0 ? (
                    <div className="flex flex-col items-center justify-center py-20 text-center">
                        <Store className="h-12 w-12 text-ink-faint mx-auto mb-3" />
                        <p className="text-sm text-ink-muted">Aucune franchise trouvée</p>
                    </div>
                ) : (
                    <>
                        <div className={viewMode === "grid" ? "grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4" : "flex flex-col gap-3"}>
                            {franchises.map((f, index) => {
                                const estabCount = f.establishmentCount ?? 0;
                                const newsCount = f.recentNewsCount ?? 0;
                                const isActive = newsCount >= 3;

                                return (
                                    <React.Fragment key={f._id}>
                                    {isTrial && index === visibleCount && (
                                        <TrialLockBanner hiddenCount={total - visibleCount} className="col-span-full rounded-2xl border-x" />
                                    )}
                                    <button
                                        onClick={() => navigate(`/franchiseurs/${f._id}`)}
                                        className={`text-left bg-surface border border-border rounded-2xl p-5 hover:border-border-strong transition-colors duration-150 group ${index >= visibleCount ? TRIAL_LOCKED_CLASS : ""}`}
                                    >
                                        <div className="flex items-start gap-3 mb-4">
                                            <FranchiseLogo f={f} size={48} />
                                            <div className="flex-1 min-w-0 pt-0.5">
                                                <div className="flex items-center gap-2 flex-wrap mb-0.5">
                                                    <p className="text-sm font-bold text-ink leading-tight truncate">
                                                        {f.name}
                                                    </p>
                                                    <NewsCategoryTag category={f.latestNewsCategory} />
                                                </div>
                                                {f.topCategory && (
                                                    <p className="text-xs text-ink-muted">{f.topCategory}</p>
                                                )}
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-3 gap-x-4 gap-y-3 border-t border-border pt-3 mb-4">
                                            <div>
                                                <p className="text-[9px] font-semibold text-ink-faint uppercase tracking-wider mb-0.5">Établissements</p>
                                                <p className="font-mono text-sm font-medium text-ink">
                                                    {estabCount.toLocaleString("fr-FR")}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-semibold text-ink-faint uppercase tracking-wider mb-0.5">Franchisés</p>
                                                <p className="font-mono text-sm font-medium text-ink">
                                                    {(f.franchiseeCount ?? 0).toLocaleString("fr-FR")}
                                                </p>
                                            </div>
                                            <div>
                                                <p className="text-[9px] font-semibold text-ink-faint uppercase tracking-wider mb-0.5">Actualités</p>
                                                <p className="font-mono text-sm font-medium text-ink">{newsCount}</p>
                                            </div>
                                        </div>

                                        {(f.associated?.length ?? 0) > 0 && (
                                            <div className="flex items-center justify-between gap-3 border-t border-border pt-3 mb-4">
                                                <p className="text-xs text-ink-muted">Leurs franchisés opèrent aussi</p>
                                                <BrandStack brands={f.associated!} size={22} max={4} />
                                            </div>
                                        )}

                                        <div className="flex items-center justify-between border-t border-border pt-3">
                                            <div className="flex items-center gap-1.5">
                                                <span className={`w-1.5 h-1.5 rounded-full ${isActive ? "bg-success" : "bg-warning"}`} />
                                                <span className="text-xs text-ink-muted">
                                                    {isActive ? "Actif" : "Peu actif"}
                                                </span>
                                            </div>
                                            <span className="text-sm text-ink font-medium group-hover:underline">
                                                Ouvrir →
                                            </span>
                                        </div>
                                    </button>
                                    </React.Fragment>
                                );
                            })}
                        </div>

                        {/* Pagination */}
                        {totalPages > 1 && !isTrial && (
                            <div className="flex items-center justify-center gap-2 mt-8">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page === 1}
                                    className="p-2 rounded-lg border border-border bg-surface text-ink-muted hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </button>

                                {Array.from({ length: totalPages }, (_, i) => i + 1)
                                    .filter((p) => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                                    .reduce<(number | "…")[]>((acc, p, idx, arr) => {
                                        if (idx > 0 && p - (arr[idx - 1] as number) > 1) acc.push("…");
                                        acc.push(p);
                                        return acc;
                                    }, [])
                                    .map((p, idx) =>
                                        p === "…" ? (
                                            <span key={`ellipsis-${idx}`} className="px-1 text-sm text-ink-faint">…</span>
                                        ) : (
                                            <button
                                                key={p}
                                                onClick={() => setPage(p as number)}
                                                className={`w-9 h-9 rounded-lg text-sm font-medium transition-colors duration-150 ${
                                                    page === p
                                                        ? "bg-ink text-ink-inverse"
                                                        : "bg-surface border border-border text-ink-muted hover:bg-surface-sunken"
                                                }`}
                                            >
                                                {p}
                                            </button>
                                        )
                                    )}

                                <button
                                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                                    disabled={page === totalPages}
                                    className="p-2 rounded-lg border border-border bg-surface text-ink-muted hover:bg-surface-sunken disabled:opacity-40 disabled:cursor-not-allowed transition-colors duration-150"
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>
        </div>
    );
}
